/**
 * 用户信息
 */
import { defineStore, storeToRefs } from 'pinia';
import {
  emitter,
  EventEnum,
  getBetPlatform,
  getHostName,
  getJSON,
  getLocalStorage,
  getp,
  getUrlParams,
  LoginData,
  num2Li,
  randomInteger,
  strPadLeft,
  uuid,
} from '@star/utils';
import { start } from '../start';
import { useHot } from '../useHot';
import network from '../network';
import { useTenantStore } from '../tenant';
import { useBalanceListStore } from './balanceLists';
import { useSessionStorage } from '@vueuse/core';
import { RegisterWayEnum, StorageKeysEnum } from '@star/constants';
import { userServices } from '../services';
import { UserInfoBean } from '@star/types';
import { pickBy } from 'lodash-es';
import { getDeviceId } from '@star/utils/src/deviceCode';

const rebatePercenStr = rebate => {
  if (isNaN(rebate)) {
    return '0.00 %';
  }
  let rebateStr = (rebate / 100).toString();
  const pos = rebateStr.indexOf('.');
  if (pos < 0) {
    rebateStr += '.';
  }
  for (let i = 0; i < 1 - pos; i++) {
    rebateStr += '0';
  }
  return rebateStr + ' %';
};

type StateData = {
  userInfo: UserInfoBean;
  [propName: string]: any;
};

export const useUserStore = defineStore({
  id: 'user',
  state: (): StateData => {
    return {
      // @ts-ignore
      userInfo: useSessionStorage<UserInfoBean>(StorageKeysEnum.APP_USER_INFO, {
        cust_id: 0,
        cust_name: '',
        hash_wallet_address: '',
        partner: {},
        partner_flag: 0,
        cust_account_type: 0,
      } as unknown as UserInfoBean),
      contact: {}, // 用户的联系信息
      verifyCode: {
        image: '',
        verifycode_key: '',
      },
      isCanShowYEB: false,
      checkPayIdIndex: 0, // 自助提现选中的是银行卡支付方式还是支付宝方式的下标
      currentBankInfo: {},
      allowCreateMuc: false, // 是否可以创建聊天群m
      deviceId: '', // 设备ID
      isCreateFict: false, // 是否有创建虚拟会员权限
      bindBanklist: [], // 用户绑定银行卡列表
      offineList: [], // 线下支付大类列表
      onLineList: [], // 线上支付大类列表
      quickList: [], // 快速充值
      certification: {}, // 会员认证信息
      deviceUDID: '', // 用户的设备的uuid
      defaultCard: [], // 用户的相关银行卡信息
      checkedDefaultCardInx: 0, // 选中的 defaultCard 相关银行卡信息下标
      cardTips: {
        // 银行卡提示文案
        xunihuobi: '',
      },
      qipaiHistoryData: {},
      userReportShow: true,
      userReportData: {
        currentPlay: {},
      },
      commissionList: [],
      myShareList: {},
      myShareListCode: {}, // 我的分享只有code 和 url
      myShareRebate: {}, // 我的分享 - 佣金
      allSummaryList: {
        // 个人游戏记录列表
        realtimePlayList: [],
      },
      discountInfo: { is_showDiscount: 1 }, // 是否展示优惠
      lotteryDialogShow: false, // 个人报表，团队游戏 彩票详情的弹窗显示
      pcothersGameHistory: {},
      thirdGamesResData: {},
      statisticsList: [], // 第三方游戏统计
      OTCbitLists: [], // otc全部币种
      OTCFBLists: [], // otc全部法币
      myCommissionlist: {} as any, // 我的佣金
      signInList: {},
      vipSignInList: [],
      rescueFundListData: [],
      sharePlayers: 0, // 分享领工资有效玩家
      shareRecharge: 0, // 分享领工资充值
      shareCoding: 0, // 分享领工资打码
      activityListLeft: [],
      userTaskList: [], // 会员任务列表
      borrowTypeList: [],
      borrowTypeDate: '',
      bankConfig: {},
      // 充值成功后ws推送的数据 {amount, number, charge_type, cust_id}
      rechargeSuccess: null, // 充值类型 charge_type 0:线上 1线下 2手工
      // 提现成功后ws推送的数据 {amount, withdrw_type, cust_id}
      withdrawSuccess: null, // 提现类型 withdrw_type 1:成功 2拒绝
      localCountry: [], // 本地国别列表
      vipList: [], // vip晋级模式列表
      highestVipLevel: 0, // VIP最高等级
      vipListWithStatus: [],
      hasLoginPassword: false, // 是否设置过登录密码
      hasPayPassword: false, // 是否设置过支付密码

      // 注册送彩金相关
      stateInfoItem: {},
    };
  },
  getters: {
    isHighestVipLevel: state => state.userInfo?.cust_level === state.highestVipLevel,
    isLogin: state => !!state.userInfo?.access_token && !!state.userInfo?.cust_id, // 是否已经登录
    getUserInfo: state => state.userInfo,
    isFrozen: state => (state.userInfo?.is_frozen === 2 ? true : false), // 是否冻结 1正常 2异常
    // 各彩种的基础返点
    uBaseRebates: state => {
      const rebates = {
        rebate: 0, // 时时彩
        rebate_ft: 0, // 福体
        rebate_sc: 0, // 北京赛车
        rebate_k3: 0, // 快3
        rebate_115: 0, // 11选5
        rebate_zr: 0, // 真人
        rebate_dz: 0, // 电子游艺
        rebate_ty: 0, // 体育
        rebate_qp: 0, // 棋牌
        rebate_ms: 0, // 六合彩
      };
      if (state.userInfo) {
        // 代理
        if (state.userInfo.partner_flag === 1 && state.userInfo.partner) {
          for (const k in rebates) {
            rebates[k] = state.userInfo.partner[k];
          }
        } else {
          // 普通用户
          for (const k in rebates) {
            rebates[k] = state.userInfo[k];
          }
        }
      }
      return rebates;
    },
    isFreeUser: state => {
      if (state.userInfo?.cust_name) {
        return state.userInfo?.cust_name.substr(0, 5) === 'tUser';
      }
      return false;
    },
    getRebatePercentStr: state => {
      const userInfo: any = state.userInfo || {};
      return rebatePercenStr(userInfo.rebate);
    },
    getSCRebatePercentStr: state => {
      const userInfo: any = state.userInfo || {};
      return rebatePercenStr(userInfo.rebate_sc);
    },
    get115RebatePercentStr: state => {
      const userInfo: any = state.userInfo || {};
      return rebatePercenStr(userInfo.rebate_115);
    },
    getK3RebatePercentStr: state => {
      const userInfo: any = state.userInfo || {};
      return rebatePercenStr(userInfo.rebate_k3);
    },
    getMSRebatePercentStr: state => {
      const userInfo: any = state.userInfo || {};
      return rebatePercenStr(userInfo.rebate_ms);
    },
    getFTRebatePercentStr: state => {
      const userInfo: any = state.userInfo || {};
      return rebatePercenStr(userInfo.rebate_ft);
    },
    getTCLRebatePercentStr: state => {
      const userInfo: any = state.userInfo || {};
      return rebatePercenStr(userInfo.rebate_tcl);
    },
    getZRRebatePercentStr: state => {
      const userInfo: any = state.userInfo || {};
      return rebatePercenStr(userInfo.rebate_zr);
    },
    getDZRebatePercentStr: state => {
      const userInfo: any = state.userInfo || {};
      return rebatePercenStr(userInfo.rebate_dz);
    },
    getTYRebatePercentStr: state => {
      const userInfo: any = state.userInfo || {};
      return rebatePercenStr(userInfo.rebate_ty);
    },
    getQPRebatePercentStr: state => {
      const userInfo: any = state.userInfo || {};
      return rebatePercenStr(userInfo.rebate_qp);
    },
    getBYRebatePercentStr: state => {
      const userInfo: any = state.userInfo || {};
      return rebatePercenStr(userInfo.rebate_by);
    },
    // 用户绑定的虚拟币卡列表 pay_org_id: 187
    XNBBankList: state => state.bindBanklist.filter((item: any) => item.pay_org_id === 187),
    // 虚拟币支付大类
    XNBOffine: state => state.offineList, //.find(item => item.pay_org_id === 188) || {},
    // 虚拟币线上支付大类
    XNBOnLine: state => state.onLineList, //.find(item => item.pay_org_id === 187) || {},
  },
  actions: {
    // 登录注册弹窗
    showLogin(params: LoginData) {
      emitter.emit(EventEnum.LOGIN, params);
    },
    setRechargeSuccessData(data = null) {
      this.rechargeSuccess = data || null;
    },
    setWithdrawSuccessData(data = null) {
      this.withdrawSuccess = data || null;
    },
    async setLoginPassword(router: any) {
      if (this.isLogin && !this.hasLoginPassword) {
        await router.push({ path: '/setting', query: { setLoginPassword: '1' } });
      }
    },
    // 用户签到列表
    setSignInList(data) {
      if (data?.isClear) {
        this.signInList = {};
      } else {
        if (data) {
          const newData: any = {};
          newData.sindInList = [
            data.monday / 1000,
            data.tuesday / 1000,
            data.wednesday / 1000,
            data.thursday / 1000,
            data.friday / 1000,
            data.saturday / 1000,
            data.sunday / 1000,
          ];
          newData.isSignIn = Number(data.day_type) === 1;
          newData.signDay = data.sign_day;
          newData.isLevelReward = data.isLevelReward;
          newData.webState = Number(data.web_state);
          newData.sign_type = Number(data.sign_type);
          this.signInList = newData;
        } else {
          this.signInList = {};
        }
      }
    },
    setActivePlayers(data) {
      this.sharePlayers = data.standara.record || 0;
      this.shareRecharge = data.cumulative_recharge || 0;
      this.shareCoding = data.cumulative_coding || 0;
    },
    setRescueFundListNew(data, isShow, payload) {
      const res = data;
      const newArr: any[] = [];
      // const ori = JSON.parse(JSON.stringify(data))
      if (res.length) {
        let rechargePack: any = [];
        let rescueFund: any[] = [];
        let giveAway: any[] = [];
        let shareIt: any[] = [];
        let virtualCurrency: any[] = [];
        for (let i = 0; i < res.length; i++) {
          if (+res[i].type === 101) {
            // 充值大礼包
            if (isShow['2']) {
              let standara = res[i].standara;
              let amount = standara.chargeAmount / 1000;
              let arr: any[] = [];
              let offset: any[] = []; // 充值金额与达标金额的差值
              for (let j = 1; j < 11; j++) {
                let list: any = {};
                let stan = 'standara' + j;
                let standara_name = 'standara_name' + j;
                let icon_url = 'icon_url' + j;
                let explains = 'explains' + j;
                if (standara[stan]) {
                  let listArr = standara[stan].split(':');
                  list.fraction = Number(listArr[0]); // 达标
                  list.reward = Number(listArr[1]); // 奖金
                  list.standara_name = standara[standara_name];
                  list.icon_url = standara[icon_url];
                  list.explains = standara[explains];
                  list.activity_standard = standara.activity_standard;
                  list.recharge_type = standara.recharge_type;
                  if (list.fraction !== 0) {
                    arr.push(list.fraction);
                    let off = amount - list.fraction;
                    off >= 0 && offset.push(off); // 充值金额 - 达标金额
                    list.isReceive = 0;
                    list.percentage = 0;
                    list.startTime = res[i].eff_time;
                    list.endTime = res[i].exp_time;
                    list.chargeAmount = standara.chargeAmount;
                    list.index = j - 1;
                    rechargePack.push(list);
                  }
                }
              }
              for (let k = 0; k < arr.length; k++) {
                let item = arr[k];
                if (Date.now() < res[i].eff_time) {
                  rechargePack[k].isReceive = 3; // 即将开始
                } else {
                  if (item <= amount) {
                    rechargePack[k].percentage = 100;
                    rechargePack[k].isReceive = 2; // 可领取
                  } else {
                    if (k === 0) {
                      if (amount === 0) {
                        rechargePack[k].percentage = 0;
                      } else {
                        rechargePack[k].percentage = parseInt(String((amount / item) * 100));
                      }
                    } else if (item > amount && amount > arr[k - 1]) {
                      rechargePack[k].percentage = parseInt(
                        String(((amount - arr[k - 1]) / (item - arr[k - 1])) * 100)
                      );
                    }
                  }
                }
                rechargePack[k].completed = amount < 0 ? 0 : amount;
                if (standara.receive_amount) {
                  standara.receive_amount.forEach(item => {
                    if (Number(item.receive_level) - 1 === k) {
                      rechargePack[k].isReceive = 1; // 已领取
                    }
                  });
                }
              }
              // 如果后台设置不能多重领取 则满足领取条件的选项走如下流程
              // activity_standard 1非多重 0多重 recharge_type 0全部 1线下 2线上 3首充
              if (res[i].activity_standard === 1) {
                rechargePack.forEach((item, idx) => {
                  if (item.isReceive === 2) {
                    if (idx < offset.length - 1) {
                      // 手动领取时 满足领取的其他 不包括最后那一个
                      item.noRepeat = 1; // 不让领取
                    }
                  }
                  // 如果是首充类型 最大领取后面的进度条设置0
                  if (item.recharge_type === 3 && idx >= offset.length) {
                    item.percentage = 0;
                  }
                });
              }
              res[i].standara = rechargePack;
              newArr.push(res[i]);
            }
          } else if (+res[i].type === 102) {
            // 有玩就有礼
            if (isShow['3']) {
              let standara = res[i].standara;
              let amount = standara.PlayAmount / 1000;
              let arr: any[] = [];
              for (let c = 1; c < 11; c++) {
                let list: any = {};
                let stan1 = 'standara' + c;
                let standara_name = 'standara_name' + c;
                let icon_url = 'icon_url' + c;
                let explains = 'explains' + c;
                if (standara[stan1]) {
                  let listArr1 = standara[stan1].split(':');
                  list.fraction = Number(listArr1[0]); // 达标
                  list.reward = Number(listArr1[1]); // 奖金
                  list.standara_name = standara[standara_name];
                  list.icon_url = standara[icon_url];
                  list.explains = standara[explains];
                  if (list.fraction !== 0) {
                    arr.push(list.fraction);
                    list.isReceive = 0;
                    list.percentage = 0;
                    list.startTime = res[i].eff_time;
                    list.endTime = res[i].exp_time;
                    list.PlayAmount = standara.PlayAmount;
                    rescueFund.push(list);
                    list.index = c - 1;
                  }
                }
              }
              for (let l = 0; l < arr.length; l++) {
                let item = arr[l];
                if (Date.now() < res[i].eff_time) {
                  rescueFund[l].isReceive = 3; // 即将开始
                } else {
                  if (item <= amount) {
                    rescueFund[l].percentage = 100;
                    rescueFund[l].isReceive = 2; // 可领取
                  } else {
                    if (l === 0) {
                      if (amount === 0) {
                        rescueFund[l].percentage = 0;
                      } else {
                        rescueFund[l].percentage = parseInt(String((amount / item) * 100));
                      }
                    } else if (item > amount && amount > arr[l - 1]) {
                      rescueFund[l].percentage = parseInt(String(((amount - arr[l - 1]) / (item - arr[l - 1])) * 100));
                    }
                  }
                  rescueFund[l].completed = amount < 0 ? 0 : amount;
                  if (standara.receive_amount) {
                    standara.receive_amount.forEach(item => {
                      if (Number(item.receive_level) - 1 === l) {
                        rescueFund[l].isReceive = 1; // 已领取
                      }
                    });
                  }
                }
              }
              res[i].standara = rescueFund;
              newArr.push(res[i]);
            }
          } else if (+res[i].type === 103) {
            if (isShow['5']) {
              let standara = res[i].standara;
              let isMultiple = res[i].is_multiple;
              let amount = standara.Profit / 1000;
              let arr: any[] = [];
              for (let v = 1; v < 11; v++) {
                let list: any = {};
                let stan2 = 'standara' + v;
                let standara_name = 'standara_name' + v;
                let icon_url = 'icon_url' + v;
                let explains = 'explains' + v;
                if (standara[stan2]) {
                  let listArr2 = standara[stan2].split(':');
                  list.fraction = Number(listArr2[0]); // 达标
                  list.reward = Number(listArr2[1]); // 奖金
                  list.standara_name = standara[standara_name];
                  list.icon_url = standara[icon_url];
                  list.explains = standara[explains];
                  if (list.fraction !== 0) {
                    arr.push(list.fraction);
                    list.isReceive = 0;
                    list.percentage = 0;
                    list.startTime = res[i].eff_time;
                    list.endTime = res[i].exp_time;
                    list.Profit = standara.Profit;
                    list.index = v - 1;
                    giveAway.push(list);
                  }
                }
              }
              for (let o = 0; o < arr.length; o++) {
                let item = arr[o];
                if (Date.now() < res[i].eff_time) {
                  giveAway[o].isReceive = 3; // 即将开始
                } else if (amount !== 0) {
                  if (item <= amount) {
                    giveAway[o].percentage = 100;
                    if (isMultiple === 2) {
                      giveAway[o].isReceive = 2;
                      if (standara.logs && standara.logs.length > 0) {
                        for (let m = 0; m < standara.logs.length; m++) {
                          let num = o + 1;
                          if (standara.logs[m].receive_level === num) {
                            giveAway[o].isReceive = 1;
                          }
                        }
                      }
                    } else {
                      if (amount === item) giveAway[o].isReceive = 2;
                    }
                  } else {
                    if (o === 0) {
                      giveAway[o].percentage = parseInt(String((amount / item) * 100));
                    } else if (item >= amount && amount >= arr[o - 1]) {
                      giveAway[o].percentage = parseInt(String(((amount - arr[o - 1]) / (item - arr[o - 1])) * 100));
                      if (isMultiple === 1) {
                        giveAway[o - 1].isReceive = 2;
                        if (+standara.count === 1) giveAway[o - 1].isReceive = 1;
                      }
                    }
                  }
                }
                giveAway[o].completed = amount < 0 ? 0 : amount;
              }
              if (
                giveAway[arr.length - 1] &&
                giveAway[arr.length - 1].fraction &&
                giveAway[arr.length - 1].fraction <= amount
              ) {
                giveAway[arr.length - 1].isReceive = 2;
                if (+standara.count >= 1) giveAway[arr.length - 1].isReceive = 1;
              }
              res[i].standara = giveAway;
              newArr.push(res[i]);
            }
          } else if (+res[i].type === 203) {
            // 新增的救援金活动
            let giveAway: any[] = [];
            if (isShow['83']) {
              let standara = res[i].standara;
              let isMultiple = res[i].is_multiple;
              let amount = standara.Profit / 1000;
              let arr: any[] = [];
              for (let v = 1; v < 11; v++) {
                let list: any = {};
                let stan2 = 'standara' + v;
                let standara_name = 'standara_name' + v;
                let icon_url = 'icon_url' + v;
                let explains = 'explains' + v;
                if (standara[stan2]) {
                  let listArr2 = standara[stan2].split(':');
                  list.fraction = Number(listArr2[0]); // 达标
                  list.reward = Number(listArr2[1]); // 奖金
                  list.standara_name = standara[standara_name];
                  list.icon_url = standara[icon_url];
                  list.explains = standara[explains];
                  if (list.fraction !== 0) {
                    arr.push(list.fraction);
                    list.isReceive = 0;
                    list.percentage = 0;
                    list.startTime = res[i].eff_time;
                    list.endTime = res[i].exp_time;
                    list.Profit = standara.Profit;
                    list.index = v - 1;
                    giveAway.push(list);
                  }
                }
              }
              for (let o = 0; o < arr.length; o++) {
                let item = arr[o];
                if (Date.now() < res[i].eff_time) {
                  giveAway[o].isReceive = 3; // 即将开始
                } else if (amount !== 0) {
                  if (item <= amount) {
                    giveAway[o].percentage = 100;
                    if (isMultiple === 2) {
                      giveAway[o].isReceive = 2;
                      if (standara.logs && standara.logs.length > 0) {
                        for (let m = 0; m < standara.logs.length; m++) {
                          let num = o + 1;
                          if (standara.logs[m].receive_level === num) {
                            giveAway[o].isReceive = 1;
                          }
                        }
                      }
                    } else {
                      if (amount === item) giveAway[o].isReceive = 2;
                    }
                  } else {
                    if (o === 0) {
                      // @ts-ignore
                      giveAway[o].percentage = parseInt((amount / item) * 100);
                    } else if (item >= amount && amount >= arr[o - 1]) {
                      giveAway[o].percentage = parseInt(
                        // @ts-ignore
                        ((amount - arr[o - 1]) / (item - arr[o - 1])) * 100
                      );
                      if (isMultiple === 1) {
                        giveAway[o - 1].isReceive = 2;
                        if (+standara.count === 1) giveAway[o - 1].isReceive = 1;
                      }
                    }
                  }
                }
                giveAway[o].completed = amount < 0 ? 0 : amount;
              }
              if (
                giveAway[arr.length - 1] &&
                giveAway[arr.length - 1].fraction &&
                giveAway[arr.length - 1].fraction <= amount
              ) {
                isMultiple === 1 && (giveAway[arr.length - 1].isReceive = 2); // isMultiple =1 才有领取最后一个
                if (+standara.count >= 1) giveAway[arr.length - 1].isReceive = 1;
              }
              res[i].standara = giveAway;
              newArr.push(res[i]);
            }
          } else if (+res[i].type === 106) {
            // 分享领工资
            if (isShow['4']) {
              this.setActivePlayers(res[i]);
              let standara = res[i].standara;
              let amount = standara.record;
              let arr: any[] = [];
              for (let z = 1; z < 11; z++) {
                let list: any = {};
                let stan2 = 'standara' + z;
                let standara_name = 'standara_name' + z;
                let icon_url = 'icon_url' + z;
                let explains = 'explains' + z;
                if (standara[stan2]) {
                  let listArr2 = standara[stan2].split(':');
                  list.fraction = Number(listArr2[0]); // 达标
                  list.reward = Number(listArr2[1]); // 奖金
                  list.standara_name = standara[standara_name];
                  list.icon_url = standara[icon_url];
                  list.explains = standara[explains];
                  if (list.fraction !== 0) {
                    arr.push(list.fraction);
                    list.isReceive = 0;
                    list.percentage = 0;
                    list.startTime = res[i].eff_time;
                    list.endTime = res[i].exp_time;
                    list.index = z - 1;
                    shareIt.push(list);
                  }
                }
              }
              // console.log('standara', ori, amount, shareIt)
              for (let x = 0; x < arr.length; x++) {
                let item = arr[x];
                if (item <= amount) {
                  shareIt[x].isReceive = 2;
                  shareIt[x].percentage = 100;
                }
                if (x === 0) {
                  if (amount === 0) {
                    shareIt[x].percentage = 0;
                  } else {
                    if (item === amount) shareIt[x].isReceive = 2;
                    shareIt[x].percentage = parseInt(String((amount / item) * 100));
                    if (shareIt[x].percentage > 100) shareIt[x].percentage = 100;
                  }
                } else if (item > amount && amount > arr[x - 1]) {
                  // shareIt[x - 1].isReceive = 2
                  shareIt[x].percentage = parseInt(String(((amount - arr[x - 1]) / (item - arr[x - 1])) * 100));
                }
                shareIt[x].completed = amount < 0 ? 0 : amount;
                if (standara.receive_amount) {
                  standara.receive_amount.forEach(item => {
                    if (Number(item.receive_level) - 1 === x) {
                      shareIt[x].isReceive = 1; // 已领取
                    }
                  });
                }
              }
              res[i].standara = shareIt;
              newArr.push(res[i]);
            }
          } else if (+res[i].type === 111) {
            // 虚拟币充值
            if (isShow['32']) {
              let standara = res[i].standara;
              let amount = standara.virtualAmount ? standara.virtualAmount / 1000 : 0;
              let arr: any[] = [];
              for (let n = 1; n < 11; n++) {
                let list: any = {};
                let stan = 'standara' + n;
                let standara_name = 'standara_name' + n;
                let icon_url = 'icon_url' + n;
                let explains = 'explains' + n;
                if (standara[stan]) {
                  let listArr = standara[stan].split(':');
                  list.fraction = Number(listArr[0]); // 达标
                  list.reward = Number(listArr[1]); // 奖金
                  list.standara_name = standara[standara_name];
                  list.icon_url = standara[icon_url];
                  list.explains = standara[explains];
                  if (list.fraction !== 0) {
                    arr.push(list.fraction);
                    list.isReceive = 0;
                    list.percentage = 0;
                    list.startTime = res[i].eff_time;
                    list.endTime = res[i].exp_time;
                    list.index = n - 1;
                    virtualCurrency.push(list);
                  }
                }
              }
              for (let m = 0; m < arr.length; m++) {
                let item = arr[m];
                if (Date.now() < res[i].eff_time) {
                  virtualCurrency[m].isReceive = 3; // 即将开始
                } else {
                  if (item <= amount) {
                    virtualCurrency[m].percentage = 100;
                    virtualCurrency[m].isReceive = 2; // 可领取
                  } else {
                    if (m === 0) {
                      if (amount === 0) {
                        virtualCurrency[m].percentage = 0;
                      } else {
                        virtualCurrency[m].percentage = parseInt(String((amount / item) * 100));
                      }
                    } else if (item > amount && amount > arr[m - 1]) {
                      virtualCurrency[m].percentage = parseInt(
                        String(((amount - arr[m - 1]) / (item - arr[m - 1])) * 100)
                      );
                    }
                  }
                }
                virtualCurrency[m].completed = amount < 0 ? 0 : amount;
                if (standara.receive_amount) {
                  standara.receive_amount.forEach(item => {
                    if (Number(item.receive_level) - 1 === m) {
                      virtualCurrency[m].isReceive = 1; // 已领取
                    }
                  });
                }
              }
              res[i].standara = virtualCurrency;
              newArr.push(res[i]);
            }
          } else if (+res[i].type === 110) {
            if (isShow['22']) {
              res[i].standara = [true];
              newArr.push(res[i]);
            }
          } else if (+res[i].type === 112) {
            if (isShow['35']) {
              res[i].standara = [];
              newArr.push(res[i]);
            }
          } else if (+res[i].type === 113) {
            if (isShow['50']) {
              res[i].standara = [];
              newArr.push(res[i]);
            }
          } else if (payload.showSubtasks) {
            if (+res[i].type === 114) {
              // 游戏任务彩票
              res[i].standara = [true];
              newArr.push(res[i]);
            }
            if (+res[i].type === 115) {
              // 游戏任务棋牌
              res[i].standara = [true];
              newArr.push(res[i]);
            }
            if (+res[i].type === 116) {
              // 游戏任务电子
              res[i].standara = [true];
              newArr.push(res[i]);
            }
            if (+res[i].type === 117) {
              // 游戏任务真人
              res[i].standara = [true];
              newArr.push(res[i]);
            }
            if (+res[i].type === 118) {
              // 游戏任务捕鱼
              res[i].standara = [true];
              newArr.push(res[i]);
            }
            if (+res[i].type === 119) {
              // 游戏任务体育
              res[i].standara = [true];
              newArr.push(res[i]);
            }
          }
        }
        if (newArr && newArr.length > 0) {
          newArr.sort((a, b) => a.sort_v - b.sort_v);
        }
      }
      this.rescueFundListData = newArr as any;
    },
    /**
     * 清除用户数据
     */
    clearUserInfo() {
      const balanceListStore = useBalanceListStore();
      this.userInfo = {} as UserInfoBean;
      this.bindBanklist = [];
      this.offineList = [];
      this.certification = {};
      balanceListStore.$reset(); // 清空用户币的数据
    },
    // 更新用户数据
    saveUser(newData) {
      // 初始化用户数据
      this.userInfo = { ...this.userInfo, ...newData };
    },
    setDeviceUDID(deviceUDID) {
      this.deviceUDID = deviceUDID || '';
    },
    // 修改头像
    async reNickHead(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      const data = {
        head: payload.head,
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
      };
      try {
        const res = await network.request({
          cmdId: 310114,
          data: data,
        });
        if (res && res.type === 'success') {
          this.saveUser({
            ...this.userInfo,
            head: payload.head,
          });
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    // 转入保险箱
    async safeTransferInfo(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      const data = {
        tid: tenantInfo.value.tid,
        acct_id: userInfo.value.acct_id,
        balance: payload.balance,
        mark: 1,
        oper_code: userInfo.value.cust_code,
      };
      try {
        const res = await network.request({
          cmdId: 340334,
          data: data,
          paramType: 1,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    // 通过cust_id获取用户信息
    async getUserInfoByCustId(payload: any = {}) {
      const {
        hotData: { tenantInfo },
      } = useHot();
      let data = [tenantInfo.value.tid, ...payload];
      try {
        const res = await network.request({
          cmdId: 310131,
          data,
          paramType: 2,
          isApiV2: false,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    // 转出保险箱
    async safeTransferOut(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      const data = {
        tid: tenantInfo.value.tid,
        acct_id: userInfo.value.acct_id,
        balance: payload.balance,
        mark: 1,
        oper_code: userInfo.value.cust_code,
      };
      try {
        const res = await network.request({
          cmdId: 340335,
          data: data,
          paramType: 1,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    // 打开保险箱
    async openSafe(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      const data = {
        tid: tenantInfo.value.tid,
        cust_code: userInfo.value.cust_code,
        acct_pwd: getp(payload.pwd),
      };
      try {
        const res = await network.request({
          cmdId: 310136,
          data: data,
          headerType: 2,
          paramType: 0,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    // 利息宝利息相关
    async reqInterestData() {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
      };
      try {
        const res = await network.request({
          cmdId: 340337,
          data: data,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    // 利息方案列表
    async reqInterestPlanData(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        page_start: payload.page_start,
        num_per_page: payload.num_per_page,
        interest_type: payload.interest_type,
      };
      console.log(data, 'reqInterestPlanData');
      try {
        const res = await network.request({
          cmdId: 360201,
          data: data,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },

    // 已购买的列表
    async reqOrderedPlanData(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      const data = {
        tid: tenantInfo.value.tid,
        cust_name: userInfo.value.cust_name,
        page_start: payload.page_start,
        num_per_page: payload.num_per_page,
      };
      try {
        const res = await network.request({
          cmdId: 3602141,
          data: data,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },

    // 利息宝购买接口
    async reqOrderPlan(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        amount: payload.amount,
        plan_id: payload.plan_id,
      };
      try {
        const res = await network.request({
          cmdId: 360212,
          data: data,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },

    // 利息宝卖出接口
    async reqSellPlan(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        acct_product_id: payload.acct_product_id,
        ...payload,
      };
      try {
        const res = await network.request({
          cmdId: 360213,
          data: data,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    // 验证利息宝
    async checkInterest(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        type: '105', // 105利息宝, 108借呗
      };
      if (payload?.type) {
        data.type = payload.type;
      }
      try {
        const res = await network.request({
          cmdId: 360731,
          data: data,
        });
        if (res) {
          return Promise.resolve(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    // 利息宝按钮是否显示
    async reqInterest() {
      const { hotData } = useHot();
      // eslint-disable-next-line no-unused-vars
      const { tenantInfo, userInfo } = hotData;
      const data = {
        tid: tenantInfo.value.tid,
        type: '105',
      };
      try {
        const res = await network.request({
          cmdId: 370601,
          data: data,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    // 利息宝明细列表接口
    async reqSafeList(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      const data = {
        tid: tenantInfo.value.tid,
        cust_code: userInfo.value.cust_name,
        page_start: payload.page,
        num_per_page: 10,
      };
      try {
        const res = await network.request({
          cmdId: 340620,
          data: data,
          paramType: 0,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    // 保险箱相关数据
    async getSafeRelated() {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
      };
      try {
        const res = await network.request({
          cmdId: 340336,
          data: data,
          headerType: 2,
          paramType: 0,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * @description: 查询余额宝利息
     */
    async getInterest() {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
      };
      try {
        const res = await network.request({
          cmdId: 340339,
          data: data,
          headerType: 2,
          paramType: 0,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * @description: 领取利息宝利息
     */
    async receiveYuEbaoInterest() {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      const data = {
        tid: tenantInfo.value.tid,
        acct_id: userInfo.value.acct_id,
      };
      try {
        const res = await network.request({
          cmdId: 340622,
          data,
        });
        if (res && res.type === 'success') {
          this.discountInfo = res.data;
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * @name 获取用户是否在禁用名单
     */
    async reqDiscountInfo() {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
      };
      try {
        const res = await network.request({
          cmdId: 3101453,
          data: data,
        });
        if (res && res.type === 'success') {
          this.discountInfo = res.data;
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * @name 设定默认银行卡
     * @param @requires payload.pay_info_id
     * @param payload.key 本次请求的key，默认为default
     * @param payload.options 更多参数 JSON
     */
    async reqReDefaultBankCard(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      let data = [tenantInfo.value.tid, payload.pay_info_id, userInfo.value.cust_id];

      try {
        const res = await network.request({
          cmdId: 310412,
          data: data,
          paramType: 2,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * 获取活动/待领取 - 列表
     * @returns
     */
    async reqWaitReceiveList(params?: any) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        ...params,
      };
      try {
        const res = await network.request({
          cmdId: 327057,
          data,
        });
        if (res && res.type === 'success') {
          console.log(res, '---->reqWaitReceiveList');
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    // 获取签到数据
    async reqSignIn() {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id || 0,
      };
      try {
        const res = await network.request({
          cmdId: 374003,
          data,
        });
        if (res && res.type === 'success') {
          this.setSignInList(res.data);
        }
        return Promise.resolve(res?.data);
      } catch (error: any) {
        console.log(error);
      }
    },
    // 签到
    async singIn() {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
      };
      try {
        const res = await network.request({
          cmdId: 374002,
          data,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    // 签到规则
    async signInRule() {
      try {
        const res = await network.request({
          cmdId: 31589,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * 获取vip晋级模式列表信息,并携带领取状态
     */
    async reqVipListWithStatus() {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        cust_name: userInfo.value.cust_name,
        level: userInfo.value.cust_level,
        state: 1,
      };
      try {
        const res = await network.request({
          // cmdId: 374031,
          cmdId: 3740312,
          data,
        });
        if (res && res.type === 'success') {
          this.setSignInList(res.data);
          this.vipListWithStatus = res.data?.data || [];
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * 获取vip晋级模式列表信息
     */
    async reqVipList() {
      const { hotData } = useHot();
      const { tenantInfo } = hotData;
      const data = {
        tid: tenantInfo.value.tid,
        state: 1,
      };
      try {
        const res: any = await network.request({
          cmdId: 374030,
          data,
        });
        if (res && res.type === 'success') {
          this.vipList = res.data?.data || [];
          this.highestVipLevel = this.vipList.length;
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    // 优惠领取记录
    async reqReceiveRecord(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;

      let data: any = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        ...payload,
      };

      try {
        const res = await network.request({
          cmdId: 724211, // 724208
          data,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },

    // 优惠领取记录(新)
    async reqReceiveRecordNew(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;

      let data: any = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        ...payload,
      };

      try {
        const res = await network.request({
          cmdId: 724210,
          data,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    // 优惠领取功能接口    领取记录
    async reqReceiveActivity(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;

      let data: any = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        ...payload,
      };

      try {
        const res = await network.request({
          cmdId: 724209,
          data,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    // 奖品领取记录列表
    async reqPrizeRecord(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;

      let data: any = {
        tid: tenantInfo.value.tid,
        acctId: userInfo.value.acct_id,
        ...payload,
      };
      const params = {
        cmdId: 621110,
        paramType: 1,
        data: data,
      };
      console.log(params, 'reqPrizeRecorddata');
      try {
        const res = await network.request(params);
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },

    // 领取奖品接口
    async reqReceivePrize(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      console.log(userInfo, 'reqReceivePrize');
      let data: any = {
        tid: tenantInfo.value.tid,
        acctId: userInfo.value.acct_id,
        custId: userInfo.value.cust_id,
        account: userInfo.value.cust_name,
        ...payload,
      };
      const params = {
        cmdId: 621112,
        paramType: 1,
        data: data,
      };

      console.log(params, 'reqPrizeRecorddata');
      try {
        const res = await network.request(params);
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    // 检查注册送彩金是否可领取
    async checkReceiveBonusState(payload?: Record<string, any>) {
      const tenantState = useTenantStore();
      const { hotData } = useHot();
      const { userInfo } = hotData;
      const data = {
        tid: tenantState.tenantInfo.tid,
        cust_id: userInfo.value.cust_id,
      };

      try {
        const res = await network.request({
          cmdId: 377003,
          paramType: 0,
          data: data,
        });
        console.log(res, '检查注册送彩金是否可领取');
        this.stateInfoItem = res.data;
        return Promise.resolve(res);
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },

    // 获取注册送彩金活动详情
    async getRegisterBonusDetail(payload?: Record<string, any>) {
      const tenantState = useTenantStore();
      const { hotData } = useHot();
      const { userInfo } = hotData;
      const data = {
        tid: tenantState.tenantInfo.tid,
        ...payload,
      };
      try {
        const res = await network.request({
          cmdId: 377004,
          paramType: 0,
          data: data,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.resolve(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },

    // 检查用户是否可以领取
    async checkReceiveBonus(payload): Promise<any> {
      const tenantState = useTenantStore();
      const { hotData } = useHot();
      const { userInfo } = hotData;
      const data = {
        tid: tenantState.tenantInfo.tid,
        cust_id: userInfo.value.cust_id,
        ...payload,
      };
      try {
        const res = await network.request({
          cmdId: 7242010,
          paramType: 0,
          data: data,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.resolve(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },

    /**
     * 领取彩金(活动彩金用)  注册送彩金
     */
    async reqGetMyAwardMoney(payload): Promise<any> {
      const tenantState = useTenantStore();
      const { hotData } = useHot();
      const { userInfo } = hotData;
      const data = {
        tid: tenantState.tenantInfo.tid,
        cust_id: userInfo.value.cust_id,
        ...payload,
        platform: getBetPlatform(),
        domain_addr: window.location.host,
      };
      try {
        const res = await network.request({
          cmdId: 15,
          data: data,
          headerType: 2,
          paramType: 0,
          isApiV2: false,
        });
        if (res) {
          console.log(res, 'reqGetMyAwardMoney15');
          return Promise.resolve(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },

    // 获取自助返水比例
    async reqWashCodeProportion(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      let data: any = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        game_type: payload.game_type,
        sgame_type: payload.sgame_type,
      };
      try {
        const res = await network.request({
          cmdId: 390000,
          data,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },

    // 获取自助返水页面数据
    async reqWashCodeList() {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      let data: any = {
        cust_id: userInfo.value.cust_id,
        tid: tenantInfo.value.tid,
      };
      try {
        const res = await network.request({
          cmdId: 390003,
          data,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    // 获取历史返水记录记录
    async reqWashCodeRecording() {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      let data: any = {
        cust_id: userInfo.value.cust_id,
        tid: tenantInfo.value.tid,
        data_type: 1,
        page: 30,
      };
      try {
        const res = await network.request({
          cmdId: 390005,
          data,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },

    // 自助返水详情
    async reqDetailsList(payload) {
      const { hotData } = useHot();
      const { tenantInfo } = hotData;
      const data = {
        tid: tenantInfo.value.tid,
        log_id: payload.logId,
      };
      try {
        const res = await network.request({
          cmdId: 390006,
          data,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },

    // 点击一键返水
    async reqWachCode(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      let data: any = {
        cust_id: userInfo.value.cust_id,
        tid: tenantInfo.value.tid,
        end_time: payload.end_time,
      };
      try {
        const res = await network.request({
          cmdId: 390004,
          data,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },

    // 佣金比例
    async reqCommissionRatio() {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      let data: any = {
        tid: tenantInfo.value.tid,
        partner_id: userInfo.value.partner.partner_id,
      };
      try {
        const res = await network.request({
          cmdId: 631016,
          data,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    // 查询红包活动状态
    async checkActivity(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      let data: any = {
        cust_id: userInfo.value.cust_id,
        tid: tenantInfo.value.tid,
        activity_name: payload.activity_name,
      };
      payload.red_packet_id && (data.red_packet_id = payload.red_packet_id);
      try {
        const res = await network.request({
          cmdId: 360728,
          data,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    // 幸运大转盘 - 积分查询
    async reqLuckyBigWchatIntegral() {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      let data: any = {
        tid: tenantInfo.value.tid,
        custId: userInfo.value.cust_id,
      };
      try {
        const res = await network.request({
          cmdId: 322961,
          data,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    // 幸运大转盘 - 轮盘查询
    async reqLuckyBigWchat() {
      const { hotData } = useHot();
      const { tenantInfo } = hotData;
      let data: any = {
        tId: tenantInfo.value.tid,
      };
      try {
        const res = await network.request({
          cmdId: 322957,
          data,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    // 幸运大转盘 - 抽奖
    async reqDrawLuckWheel(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      let data: any = {
        ...payload,
        tid: tenantInfo.value.tid,
        custId: userInfo.value.cust_id,
      };
      try {
        const res = await network.request({
          cmdId: 322962,
          data,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },

    //抽奖助力转盘数据
    async reqTurntableData(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      console.log(userInfo.value, 'userInfo');
      let data: any = {
        account: userInfo.value.cust_name,
        tid: tenantInfo.value.tid,
        id: 0,
        aid: payload.aid,
        acctId: userInfo.value.acct_id,
      };

      const params = {
        cmdId: 723623,
        paramType: 1,
        data,
      };

      try {
        const res = await network.request(params);
        if (res) {
          return Promise.resolve(res);
        } else {
          return Promise.reject(res);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },

    //启动转盘，获取奖品接口
    async reqTurntablepPrize(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      console.log(userInfo.value, 'userInfo');
      let data: any = {
        account: userInfo.value.cust_name,
        tid: tenantInfo.value.tid,
        id: 0,
        aid: payload.aid,
        acctId: userInfo.value.acct_id,
        applyIp: '127.0.0.12',
      };
      console.log(data, 'reqTurntableData');

      const params = {
        cmdId: 723622,
        paramType: 1,
        data,
      };

      try {
        const res = await network.request(params);
        if (res) {
          return Promise.resolve(res);
        } else {
          return Promise.reject(res);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },

    //抽奖助力转盘信息
    async reqTurntableInfo(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      console.log(userInfo.value, 'userInfo');
      let data: any = {
        account: userInfo.value.cust_name,
        tid: tenantInfo.value.tid,
        id: 0,
        aid: payload.aid,
        acctId: userInfo.value.acct_id,
      };

      const params = {
        cmdId: 723621,
        paramType: 1,
        data,
      };
      try {
        const res = await network.request(params);
        if (res) {
          return Promise.resolve(res);
        } else {
          return Promise.reject(res);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },

    // 幸运大转盘 抽奖信息
    async reqLuckyWheelInfo(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;

      let data: any = {
        tid: tenantInfo.value.tid,
        activity_id: payload.activity_id,
        wheelType: payload.wheelType,
      };

      if (userInfo.value.cust_id) {
        data.cust_id = userInfo.value.cust_id;
      }

      const params = {
        cmdId: 7242023,
        data,
      };

      try {
        const res = await network.request(params);
        if (res) {
          return Promise.resolve(res);
        } else {
          return Promise.reject(res);
        }
      } catch (error: any) {
        return Promise.resolve(error.data);
      }
    },

    // 幸运大转盘 抽奖
    async reqLuckyWheelDraw(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;

      let data: any = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        activity_id: payload.activity_id,
        wheelType: payload.wheelType,
      };

      const params = {
        cmdId: 7242024,
        data,
      };

      try {
        const res = await network.request(params);
        if (res) {
          return Promise.resolve(res);
        } else {
          return Promise.reject(res);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },

    // 幸运大转盘 领取任务积分
    async reqLuckyWheelReceivePoints(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;

      let data: any = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        activity_id: payload.activity_id,
        taskId: payload.taskId,
      };

      const params = {
        cmdId: 7242026,
        data,
      };

      try {
        const res = await network.request(params);
        if (res) {
          return Promise.resolve(res);
        } else {
          return Promise.reject(res);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },

    // 幸运大转盘 抽奖记录
    async reqLuckyWheelRecord(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;

      let data: any = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        activity_id: payload.activity_id,
        wheelType: payload.wheelType,
      };

      const params = {
        cmdId: 7242025,
        data,
      };

      try {
        const res = await network.request(params);
        if (res) {
          return Promise.resolve(res);
        } else {
          return Promise.reject(res);
        }
      } catch (error: any) {
        return Promise.resolve(error.data);
      }
    },

    // 注册送彩金活动---未登录红包展示
    async getRegisterBonusPrice() {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      console.log(userInfo.value, 'userInfo');
      let data: any = {
        tid: tenantInfo.value.tid,
        terminal: 2,
      };
      const params = {
        cmdId: 377001,
        paramType: 0,
        data,
      };
      console.log(params, 'getRegisterBonusPrice');
      try {
        const res = await network.request(params);
        if (res) {
          return Promise.resolve(res);
        } else {
          return Promise.reject(res);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    async reqRescueFundListNew(payload = {}) {
      const { hotData, activeConfiguration } = useHot();
      const { tenantInfo, userInfo, isLogin } = hotData;
      const _activeConfigurationJson = getJSON('activeConfiguration', true);
      let isShow = activeConfiguration.value || _activeConfigurationJson; // 配置

      let data: any = {
        tid: tenantInfo.value.tid,
      };
      if (isLogin.value) {
        data.cust_id = userInfo.value.cust_id;
      }
      try {
        const res = await network.request({
          cmdId: isLogin.value ? 370603 : 377212,
          data,
        });
        if (res && res.type === 'success') {
          this.setRescueFundListNew(res.data, isShow, payload);
          return Promise.resolve(this.rescueFundListData);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * 增加提款
     */
    async reqAddWithdraw(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      // 发起请求
      const data = {
        ...payload,
        acct_id: userInfo.value.acct_id,
        acct_pwd: payload.acct_pwd ? getp(payload.acct_pwd) : '',
        amount: payload.amount,
        chan_code: payload.chan_code || start.chanCode,
        cust_code: userInfo.value.cust_code,
        cust_id: userInfo.value.cust_id,
        oper_code: userInfo.value.cust_code,
        pay_info_id: payload.pay_info_id || '',
        pay_name: payload.pay_name ? payload.pay_name.replace(/&middot;/g, '·') : '',
        pay_no: payload.pay_no || '',
        pay_org_id: payload.pay_org_id || '',
        pay_org_name: payload.pay_org_name || '',
        open_addr: payload.open_addr || '',
        remark: '审核中',
        state: 1,
        tid: tenantInfo.value.tid,
        withdraw_type: 1,
      };
      try {
        const res = await network.request({
          cmdId: 341200,
          data: data,
          paramType: 1,
          isApiV2: true,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },

    /**
     * @name 请求游戏记录
     * @param @requires payload.start_page
     * @param @requires payload.num_per_page
     * @param payload.start_time 起始时间
     * @param payload.end_time 结束时间
     * @param payload.is_clear 是否清空本地数据(不清空则追加)
     * @param payload.key 本次请求的key，默认为default
     * @param payload.options 更多参数 JSON
     * @param payload.callback 回调 function (res, err) {}
     */
    /**
     * @name 账户相关的一些配置信息
     * @param payload.key 本次请求的key，默认为default
     */
    async reqBankInfoStatusConfig(payload) {
      const { hotData } = useHot();
      const { tenantInfo } = hotData;
      const data = {
        tid: tenantInfo.value.tid,
        option_code: payload.option_code,
      };
      const res = await network.request({
        cmdId: 370730,
        data,
      });
      if (res && res.type === 'success') {
        this.bankConfig = {
          ...this.bankConfig,
          [payload.option_code]: {
            isVisible: JSON.parse(res.data.value),
            isRequire: JSON.parse(res.data.value2),
          },
        };
        return Promise.resolve(res.data);
      } else {
        return Promise.reject(res.data);
      }
    },
    /**
     * @name 查询特殊校验状态
     * @param payload.cust_name 用户账号
     */
    async reqSpecialStatus(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo } = hotData;
      // 发起请求
      const data = {
        tid: tenantInfo.value.tid,
        cust_name: payload.cust_name?.trim(),
      };
      try {
        const res = await network.request({
          cmdId: 40001,
          data: data,
          headerType: 1,
          paramType: 1,
          isApiV2: false,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * 登录
     */
    async login(data: Record<string, any>) {
      const tenantState = useTenantStore();
      const params: Record<string, any> = {
        ...data,
        chan_code: data.chan_code || start.chanCode,
        client_id: 'react_client',
        domain_addr: window.location.host,
        grant_type: 'password',
        password: getp(data.password?.trim()),
        scope: 'read write',
        tid: tenantState.tenantInfo.tid,
        verifycode: data.verifycode,
        verifycode_key: this.verifyCode.verifycode_key,
        cust_name: data.cust_name,
        app_key: await getDeviceId(),
      };
      try {
        const res = await network.request({
          cmdId: 4,
          data: pickBy(params, val => !!val),
          headerType: 1,
          paramType: 1,
          isApiV2: true,
        });
        if (res && res.type === 'success') {
          this.userInfo = res.data;
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * 账号注册
     */
    async register(payload) {
      const userStore = useUserStore();
      const tenantState = useTenantStore();
      const { tenantInfo } = tenantState;
      // 头像的地址
      let headURI = '/static/heads/head_' + strPadLeft(randomInteger(1, 200), 2) + '.png';
      if (tenantInfo.clusters_id == 17 && tenantInfo.tid == 9) {
        headURI = '/static/heads/17-9/cn/head_' + strPadLeft(randomInteger(998, 999), 2) + '.png';
      }
      const data = {
        ...payload,
        acct_pwd: payload.acctPwd ? getp(payload.acctPwd) : '',
        adm_code: '1',
        chan_code: payload.chan_code || start.chanCode,
        client_id: 'react_client',
        contact_name: payload.contact_name || '', // 真实会员名称
        cust_name: payload.cust_name, // 会员账号
        domain_addr: window.location.host,
        email: payload.email || '', // 注册邮箱
        facebook: payload.facebook || '', // 注册的facebook
        gender: 0,
        grant_type: 'password',
        head: headURI,
        invite_code: payload.inviteCode || payload.invite_code || '', // 邀请码
        line: payload.line || '', // line 邮箱
        link_addr: payload.link_addr || '',
        mobile_phone: payload.mobile_phone || '', // 联系电话
        nickname: payload.nickname || '', // 昵称
        password: payload.password ? getp(payload.password.trim()) : '',
        platform: getBetPlatform(),
        proxy: tenantState.tenantInfo.proxy,
        qq: payload.qq || '', // qq
        regist_from: getHostName(tenantState.tenantInfo.domain_addr || ''),
        scope: 'read write',
        state: 1,
        tid: tenantState.tenantInfo.tid,
        whatsApp: payload.whatsApp || '',
        zalo: payload.zalo || '',
        verifycode: payload.verifycode,
        verifycode_key: this.verifyCode.verifycode_key,
        isQp: payload.isQp || 1,
        regist_type: RegisterWayEnum.ACCOUNT_REGISTRATION,
        app_key: await getDeviceId(),
      };
      if (payload.validate) {
        data.validate = payload.validate;
      }
      try {
        const res = await network.request({
          cmdId: 3,
          data: data,
          headerType: 1,
          paramType: 1,
          isApiV2: true,
        });
        if (res.type === 'success') {
          this.userInfo = res.data;
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * 发送注册短信
     */
    async reqSMSRigister(payload) {
      const tenantState = useTenantStore();
      // 头像的地址
      const headURI = '/static/heads/head_' + strPadLeft(randomInteger(1, 200), 2) + '.png';
      const data = {
        ...payload,
        acct_pwd: payload.acctPwd ? getp(payload.acctPwd) : '',
        adm_code: '1',
        chan_code: payload.chan_code || start.chanCode,
        client_id: 'react_client',
        contact_name: payload.contact_name || '', // 真实会员名称
        cust_name: payload.cust_name, // 会员账号
        domain_addr: window.location.host,
        email: payload.email || '', // 注册邮箱
        facebook: payload.facebook || '', // 注册的facebook
        gender: 0,
        grant_type: 'password',
        head: headURI,
        invite_code: payload.inviteCode || payload.invite_code || '', // 邀请码
        isQp: payload.isQp || 1,
        line: payload.line || '', // line 邮箱
        link_addr: payload.link_addr || '',
        mobile_phone: payload.mobile_phone || '', // 联系电话
        phone: payload.phone || '',
        nickname: payload.nickname || '', // 昵称
        password: payload.password ? getp(payload.password) : '',
        platform: getBetPlatform(),
        proxy: tenantState.tenantInfo.proxy,
        qq: payload.qq || '', // qq
        regist_from: getHostName(tenantState.tenantInfo.domain_addr || ''),
        scope: 'read write',
        smsCode: payload.smsCode,
        state: 1,
        tid: tenantState.tenantInfo.tid,
        whatsApp: payload.whatsApp || '',
        zalo: payload.zalo || '',
        verifycode: payload.verifycode,
        verifycode_key: this.verifyCode.verifycode_key,
        regist_type: RegisterWayEnum.SMS_REGISTRATION,
        app_key: await getDeviceId(),
      };
      if (payload.validate) {
        data.validate = payload.validate;
      }
      if (payload.phone) {
        data.phone = payload.phone;
      }
      try {
        const res = await network.request({
          cmdId: 301,
          data: data,
          headerType: 1,
          paramType: 1,
          isApiV2: true,
        });
        if (res.type === 'success') {
          this.userInfo = res.data;
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * 邮箱注册
     */
    async reqEmailRigister(payload) {
      const tenantState = useTenantStore();
      // 头像的地址
      const headURI = '/static/heads/head_' + strPadLeft(randomInteger(1, 200), 2) + '.png';
      const data = {
        ...payload,
        acct_pwd: payload.acctPwd ? getp(payload.acctPwd) : '',
        adm_code: '1',
        chan_code: payload.chan_code || start.chanCode,
        client_id: 'react_client',
        contact_name: payload.contact_name || '', // 真实会员名称
        cust_name: payload.cust_name, // 会员账号
        domain_addr: window.location.host,
        email: payload.email || '', // 注册邮箱
        smsCode: payload.emailCode || '',
        facebook: payload.facebook || '', // 注册的facebook
        gender: 0,
        grant_type: 'password',
        head: headURI,
        invite_code: payload.inviteCode || payload.invite_code || '', // 邀请码
        isQp: payload.isQp || 1,
        line: payload.line || '', // line 邮箱
        link_addr: payload.link_addr || '',
        mobile_phone: payload.mobile_phone || '', // 联系电话
        nickname: payload.nickname || '', // 昵称
        password: payload.password ? getp(payload.password.trim()) : '',
        platform: getBetPlatform(),
        proxy: tenantState.tenantInfo.proxy,
        qq: payload.qq || '', // qq
        regist_from: getHostName(tenantState.tenantInfo.domain_addr || ''),
        scope: 'read write',
        state: 1,
        tid: tenantState.tenantInfo.tid,
        whatsApp: payload.whatsApp || '',
        zalo: payload.zalo || '',
        verifycode: payload.verifycode,
        verifycode_key: this.verifyCode.verifycode_key,
        regist_type: RegisterWayEnum.EMAIL_REGISTRATION,
        app_key: await getDeviceId(),
      };
      if (payload.validate) {
        data.validate = payload.validate;
      }
      try {
        const res = await network.request({
          cmdId: 302,
          data: data,
          headerType: 1,
          paramType: 1,
          isApiV2: true,
        });
        if (res.type === 'success') {
          this.userInfo = res.data;
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * 获取图形验证码
     */
    async fetchVerifyCode(data) {
      data = {
        ...data,
        verifycode_key: uuid(32, 16),
        type: data.type || 1,
      };
      try {
        const res = await network.request({
          cmdId: 7,
          data: data,
          headerType: 1,
        });
        if (res && res.type === 'success') {
          this.verifyCode = res.data;
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * 发送邮箱验证码
     */
    async reqEmailCode(data): Promise<any> {
      const {
        hotData: { tenantInfo },
      } = useHot();
      data = {
        ...data,
        tid: tenantInfo.value.tid,
        verifycode: data.verifycode,
        verifycode_key: this.verifyCode.verifycode_key,
        client_id: 'react_client',
        grant_type: 'password',
        scope: 'read write',
        email_type: data.email_type,
        email_to_account: data.email_to_account,
      };
      try {
        const res = await network.request({
          cmdId: 100,
          data: data,
          headerType: 1,
          paramType: 1,
          isApiV2: true,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * 发送手机验证码
     */
    async reqSMSCode(data): Promise<any> {
      const tenantState = useTenantStore();
      const fromData = {
        tid: tenantState.tenantInfo.tid,
        verifycode: '',
        verifycode_key: '',
        client_id: 'react_client',
        grant_type: 'password',
        scope: 'read write',
        type: data.type,
        country: data.country || 'China',
        receive_mobile: data.receive_mobile,
        token: data.token,
      };
      console.log(fromData, 'reqSMSCodefromData');
      try {
        const res = await network.request({
          cmdId: 10,
          data: fromData,
          headerType: 1,
          paramType: 1,
          isApiV2: true,
        });
        console.log(fromData, res, 'reqSMSCodefromData');
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.resolve(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    //  绑定手机号
    async reqBindPhone(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      const data = {
        tid: tenantInfo.value.tid,
        phone: payload.phone,
        smsCode: payload.smsCode,
        cust_id: userInfo.value.cust_id,
      };
      console.log(data, 'reqBindPhone');
      try {
        const res = await network.request({
          cmdId: 17,
          headerType: 2,
          paramType: 0,
          data: data,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * 我的可领取彩金记录(活动彩金用)
     */
    async reqMyAwardMoneyHistory(): Promise<any> {
      const tenantState = useTenantStore();
      const { hotData } = useHot();
      const { userInfo } = hotData;
      const data = {
        tid: tenantState.tenantInfo.tid,
        cust_id: userInfo.value.cust_id,
        platform: getBetPlatform(),
        domain_addr: window.location.host,
      };
      try {
        const res = await network.request({
          cmdId: 14,
          data: data,
          headerType: 2,
          paramType: 0,
          isApiV2: false,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.resolve(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },

    /** 客户详情查询，包括代理商信息
     * @param @requires payload.cust_id 不传的时候就是查自己
     * @param payload.key 本次请求的key，默认为default
     * @param payload.options 更多参数 JSON
     * @param payload.callback 回调 function (res, err) {}
     */
    async reqCustInfo(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      let data: any[] = [];
      data[0] = tenantInfo.value.tid;
      if (payload.cust_id) {
        data[1] = payload.cust_id;
      } else {
        data[1] = userInfo.value.cust_id;
      }
      try {
        const params = {
          cmdId: 310143,
          data: data,
          paramType: 2,
          headerType: 2,
        };
        const res: any = await network.request(params);
        if (res && res.type === 'success') {
          let userInfo = res.data;
          console.log(res.data, 'reqCustInfo');
          let params: any = getUrlParams(null);
          if (params) {
            userInfo['access_token'] = params?.token;
            userInfo['isFlutter'] = true;
          }

          this.userInfo = userInfo;

          return Promise.resolve(res.data);
        } else {
          return Promise.reject(this.userInfo);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },

    /**
     * 短信登录
     */
    async reqSMSlogin(data): Promise<any> {
      const tenantState = useTenantStore();
      const params = {
        ...data,
        chan_code: data.chan_code || start.chanCode,
        client_id: 'react_client',
        cust_name: data.receiveMobile,
        domain_addr: window.location.host,
        grant_type: 'password',
        password: '',
        scope: 'read write',
        smsCode: data.smsCode,
        tid: tenantState.tenantInfo.tid,
        verifycode: data.verifycode,
        verifycode_key: this.verifyCode.verifycode_key,
      };
      try {
        const res = await network.request({
          cmdId: 401,
          data: pickBy(params, val => !!val),
          headerType: 1,
          paramType: 1,
          isApiV2: true,
        });
        if (res && res.type === 'success') {
          this.userInfo = res.data;
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * 邮箱密码登录
     */
    async reqEmailPwdlogin(data): Promise<any> {
      const tenantState = useTenantStore();
      // const userStore = useUserStore();
      const params: Record<string, any> = {
        ...data,
        chan_code: data.chan_code || start.chanCode,
        client_id: 'react_client',
        cust_name: data.emailName,
        domain_addr: window.location.host,
        grant_type: 'password',
        password: getp(data.password.trim()),
        scope: 'read write',
        tid: tenantState.tenantInfo.tid,
        verifycode: data.verifycode,
        verifycode_key: this.verifyCode.verifycode_key,
        app_key: await getDeviceId(),
      };
      try {
        const res = await network.request({
          cmdId: 403,
          data: pickBy(params, val => !!val),
          headerType: 1,
          paramType: 1,
          isApiV2: true,
        });
        if (res && res.type === 'success') {
          this.userInfo = res.data;
        } else {
          return Promise.reject(res);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * 邮箱登录
     */
    async reqEmaillogin(data): Promise<any> {
      const tenantState = useTenantStore();
      // const userStore = useUserStore();
      const params: Record<string, any> = {
        ...data,
        chan_code: data.chan_code || start.chanCode,
        client_id: 'react_client',
        cust_name: data.emailName,
        domain_addr: window.location.host,
        grant_type: 'password',
        password: '',
        scope: 'read write',
        smsCode: data.emailCode,
        tid: tenantState.tenantInfo.tid,
        verifycode: data.verifycode,
        verifycode_key: this.verifyCode.verifycode_key,
        app_key: await getDeviceId(),
      };
      try {
        const res = await network.request({
          cmdId: 402,
          data: pickBy(params, val => !!val),
          headerType: 1,
          paramType: 1,
          isApiV2: true,
        });
        if (res && res.type === 'success') {
          this.userInfo = res.data;
        } else {
          return Promise.reject(res);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },

    /**
     * 获取用户信息认证状态
     * */
    async reqCertificationInfo(payload = {}) {
      try {
        const { hotData } = useHot();
        const { tenantInfo, userInfo } = hotData;
        const data = {
          tid: tenantInfo.value.tid,
          cust_id: userInfo.value.cust_id,
          ...payload,
        };
        const res: any = await userServices.getCertificationData(data);
        console.log(res.data, 'reqCertificationInfo');
        this.certification = res.data;
        return Promise.resolve(res.data);
      } catch (e: any) {
        return Promise.reject(e);
      }
    },
    /**
     * 提交认证信息
     * contact_name:姓名 id_card_log_addr:证件图片地址 id_card:证件号码 country:国家 document_type:证件类型
     * */
    async reqIDCertification(payload = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      // 发起请求
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        ...payload,
      };
      try {
        const res = await network.request({
          cmdId: 325310,
          data: data,
          headerType: 2,
          paramType: 0,
          isApiV2: false,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * 提交邮箱认证
     * contact_name:姓名 email:邮箱 yzm验证码
     * */
    async reqEmailCertification(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      // 发起请求
      const data = {
        ...payload,
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        contact_name: payload.contact_name,
        email: payload.email,
        yzm: payload.yzm,
      };
      try {
        const res = await network.request({
          cmdId: 325309,
          data: data,
          headerType: 2,
          paramType: 0,
          isApiV2: false,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * 返回用户钱包地址
     * contact_name:姓名 id_card_log_addr:证件图片地址 id_card:证件号码 country:国家 document_type:证件类型
     * */
    async reqBindBankList(payload = []) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      // 发起请求
      const data = [tenantInfo.value.tid, userInfo.value.cust_id, 1, 20, ...payload];
      try {
        const res = await network.request({
          cmdId: 310430,
          data: data,
          headerType: 2,
          paramType: 2,
          isApiV2: false,
        });
        if (res && res.type === 'success') {
          if (res.data && res.data.data) {
            this.bindBanklist = res.data.data;

            console.log(this.bindBanklist, ' this.bindBanklist');
          } else {
            this.bindBanklist = [];
          }
          return Promise.resolve(res.data);
        } else {
          this.bindBanklist = [];
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        this.bindBanklist = [];
        return Promise.reject(error.data);
      }
    },
    /**
     * 检查提现密码是否正确
     * */
    async reqTestWithdrawPassword(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      // 发起请求
      const data = {
        tid: tenantInfo.value.tid,
        ...payload,
        acct_pwd: getp(payload.acct_pwd.trim()),
        cust_code: userInfo.value.cust_code,
      };
      try {
        const res = await network.request({
          cmdId: 310136,
          data: data,
          headerType: 2,
          paramType: 0,
          isApiV2: false,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * 添加 绑卡
     * open_addr,pay_org_id,pay_org_name,pay_name,pay_no,ifsc
     * telephone sort_v qr_code_address state pixKey pixType usdt_type
     * branch_number:分行好[支店番号] paynow_no:paynow账号
     * custContact:{mobile_phone,email,weixin}[个人资料信息]
     * */
    async reqBindCard(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      if (payload.pay_name) {
        payload.pay_name = payload.pay_name.replace(/&middot;/g, '·');
      }
      // 发起请求
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        cust_code: userInfo.value.cust_code,
        oper_code: userInfo.value.cust_code,
        state: 1,
        ...payload,
      };
      try {
        const res = await network.request({
          cmdId: 310400,
          data: data,
          headerType: 2,
          paramType: 1,
          isApiV2: false,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * 获取银行列表
     */
    async reqBankList(payload = {}) {
      const { hotData } = useHot();
      const { tenantInfo } = hotData;
      // 发起请求
      const data = {
        tid: tenantInfo.value.tid,
        ...payload,
      };
      try {
        const res = await network.request({
          cmdId: 321332,
          data: data,
          headerType: 2,
          paramType: 0,
          isApiV2: false,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * 期权项目专用提现 法币账户提现
     * */
    async reqAddWithdrawQiQuan(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      // 发起请求
      let data: any = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        acct_id: userInfo.value.acct_id,
        cust_code: userInfo.value.cust_code,
        oper_code: userInfo.value.cust_code,
        withdraw_type: 1,
        state: 1,
        chan_code: payload.chan_code || start.chanCode,
        remark: '审核中',
        pay_org_name: payload.pay_org_name,
        pay_no: payload.pay_no,
        pay_name: payload.pay_name,
        amount: payload.amount,
        pay_org_id: payload.pay_org_id,
        open_addr: payload.open_addr,
        pay_info_id: payload.pay_info_id,
        account_type: payload.account_type, // 1币币账户 2期货账户 3法币账户 4信托账户
        currency_name: payload.currency_name, // 币种
        currency: payload.area, // 法币国别
        rate: payload.huilv, // 法币汇率
        ...payload,
      };
      // 传了密码 才加密
      if (payload.acct_pwd) {
        data.acct_pwd = getp(payload.acct_pwd);
      }
      try {
        const res = await network.request({
          cmdId: 327044,
          data: data,
          headerType: 2,
          paramType: 1,
          isApiV2: true,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },

    /**
     * 获取充值大类列表 mark 1线下 2线上
     * */
    async reqPayList(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      // 发起请求
      const data = [tenantInfo.value.tid, userInfo.value.cust_id];
      payload.mark && (data[2] = payload.mark);
      try {
        const res = await network.request({
          cmdId: payload.mark ? 310141 : 310139,
          data: data,
          headerType: 2,
          paramType: 2,
          isApiV2: false,
        });
        if (res && res.type === 'success') {
          let off: any = [];
          let online: any = [];
          let quick: any = [];
          res.data &&
            res.data.forEach(item => {
              if (item.mark === 1) {
                off.push(item);
              } else if (item.mark === 2) {
                online.push(item);
              } else if (item.mark === 3) {
                quick.push(item);
              }
            });
          console.log(online, '456456498748999999999999999999999999999999999999999');
          this.offineList = off; // 线下支付大类列表
          this.onLineList = online; // 线上支付大类列表
          this.quickList = quick;
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    // 一些配置 线下充值银行卡配置
    async reqOffineConfig(opt = 'OFFLINE_RECHARGE_LIMIT') {
      const { hotData } = useHot();
      const { tenantInfo } = hotData;
      // 发起请求
      const data = {
        tid: tenantInfo.value.tid,
        option_code: opt,
      };
      try {
        const res = await network.request({
          cmdId: 370730,
          data: data,
          headerType: 2,
          paramType: 0,
          isApiV2: false,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * 发起充币 - 期权的充币
     * */
    async reqAddPayOffline(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      // 发起请求
      const data = {
        bonusAmountPercentage: payload.bonus_amount_percentage || '',
        cust_code: userInfo.value.cust_code,
        oper_code: userInfo.value.cust_code,
        cust_id: userInfo.value.cust_id,
        acct_id: userInfo.value.acct_id,
        chan_code: payload.chan_code || start.chanCode,
        tid: tenantInfo.value.tid,
        tenant_pay_id: payload.tenant_pay_id,
        pay_org_id: payload.pay_org_id,
        pay_org_name: payload.pay_org_name,
        pay_name: payload.pay_name,
        pay_no: payload.pay_no,
        cust_org_name: payload.cust_org_name,
        cust_pay_name: payload.cust_pay_name,
        cust_pay_no: payload.cust_pay_no,
        numbers: payload.numbers,
        rate: payload.rate,
        pay_chan: payload.pay_chan,
        third_order_id: payload.third_order_id,
        remark: payload.remark,
        headPortrait: payload.headPortrait,
        state: 1, // 待付款
        ...payload,
        amount: num2Li(payload.amount),
      };
      delete data.bonus_amount_percentage;
      try {
        const res = await network.request({
          cmdId: 341400,
          data: data,
          headerType: 2,
          paramType: 1,
          isApiV2: false,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * 发起充币 - 期权的充币 线上
     * */
    async reqAddPayOnline(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      // 发起请求
      let data: any = {
        tid: tenantInfo.value.tid,
        payEngName: payload.payEngName,
        acctPayLog: JSON.stringify({
          ...payload.acctPayLog,
          cust_code: userInfo.value.cust_code,
          cust_id: userInfo.value.cust_id,
          acct_id: userInfo.value.acct_id,
          balance_type_id: payload.acctPayLog.balance_type_id || 1,
          chan_code: payload.acctPayLog.chan_code || start.chanCode,
          sourcetype: payload.acctPayLog.sourcetype,
          deposit: num2Li(payload.amount),
          status: payload.acctPayLog.status || 1, // 待付款
          tid: tenantInfo.value.tid,
          success_time: new Date().getTime(),
          bonus_amount_percentage: payload.acctPayLog.bonus_amount_percentage,
          account_type: payload.acctPayLog.account_type, // 期权币币充值
          currency_name: payload.acctPayLog.currency_name, // 期权 币的名称
        }),
        paramsJson: JSON.stringify({
          ...payload.paramsJson,
          amount: num2Li(payload.amount, 100),
          cust_id: userInfo.value.cust_id,
          redirect_url: payload.paramsJson?.redirect_url ?? window.location.host,
        }),
      };
      payload.type !== undefined && (data.type = payload.type);
      try {
        const res = await network.request({
          // cmdId: 383010,
          cmdId: 383029,
          data: data,
          headerType: 2,
          paramType: 0,
          isApiV2: true,
        });
        if (res && res.type === 'success') {
          if (
            payload?.acctPayLog?.sourcetype === 'xinglianPay' ||
            payload?.acctPayLog?.sourcetype === 'ceshiinglusdt'
          ) {
            const _address = res.data?.data?.pay_url;
            if (_address) {
              let _newAddress = window.atob(_address);
              res.data.data = {
                ...res.data.data,
                pay_url: _newAddress,
                qr_url: _newAddress,
              };
            }
          }
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * 获取在线客服列表
     * chan_con 111111，第0位代表android渠道，第1位代表苹果渠道，第2位代表web渠道，第3位代表微信渠道，其他位暂未定义
     * */
    async reqServiceLists(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      // 发起请求
      const data = {
        state: 1,
        message_id: payload.message_id || 50,
        tid: tenantInfo.value.tid,
        proxy: tenantInfo.value.proxy,
        ...payload,
        chan_code: payload.chan_code || start.chanCode,
        language: payload.language || Number(getLocalStorage('languageNum') || 0),
        apply: payload.apply || undefined,
      };
      userInfo.value.cust_id && (data.cust_id = userInfo.value.cust_id);
      try {
        const res = await network.request({
          cmdId: 380239,
          data: data,
          headerType: 2,
          paramType: 0,
          isApiV2: false,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /*
     * 获取在线客服的token
     * */
    async customerGetTokenById(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      // 发起请求
      const data = {
        tId: tenantInfo.value.tid,
        custId: userInfo.value.cust_id,
        ...payload,
      };
      try {
        const res = await network.request({
          cmdId: 314125,
          data: data,
          headerType: 2,
          paramType: 0,
          isApiV2: false,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /*
     * 退出登录
     * */
    async logout(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      // 发起请求
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        cust_code: userInfo.value.cust_code,
        ...payload,
      };
      try {
        const res = await network.request({
          cmdId: 310635,
          data: data,
          headerType: 2,
          paramType: 1,
          isApiV2: false,
          client_id: 'react_client',
          scope: 'read write',
          domain_addr: window.location.host,
          chan_code: data.chan_code || start.chanCode,
        });
        if (res && res.type === 'success') {
          if (res.data && res.data.response_code === '9999') {
            this.clearUserInfo();
            // this.$reset();
          }
          return Promise.resolve(res.data);
        } else {
          return Promise.resolve(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * @description: 修改最后登录信息
     * @param {any} payload
     */
    async modifyTheLastLoginInformation(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      // 发起请求
      const data = {
        ...payload,
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        last_platform: getBetPlatform(true),
        last_regist_from: tenantInfo.value.domain_addr,
        last_app_key: await getDeviceId(),
      };

      try {
        const res = await network.request({
          cmdId: 3101113,
          data,
          paramType: 0,
          isApiV2: false,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * 修改登录密码
     * */
    async resetPassword(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      // 发起请求
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        ...payload,
        password: getp(payload.password),
        ori_password: payload.ori_password ? getp(payload.ori_password) : '',
        last_platform: getBetPlatform(),
      };
      try {
        const res = await network.request({
          cmdId: 310111,
          data: data,
          headerType: 1,
          paramType: 0,
          isApiV2: false,
        });
        if (res && res.type === 'success') {
          // if (res.data && res.data.response_code === '9999') {
          //   this.clearUserInfo();
          // }
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * 忘记密码
     * */
    async forgetPassword(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      // 发起请求
      const data = {
        tid: tenantInfo.value.tid,
        ...payload,
        password: getp(payload.password),
      };
      try {
        const res = await network.request({
          cmdId: 31011103,
          data: data,
          headerType: 2,
          paramType: 0,
          isApiV2: false,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * 是否修改过密码
     * */
    async ifRePassword(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      // 发起请求
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        pwd_type: payload.pwd_type,
        ...payload,
      };
      try {
        const res = await network.request({
          cmdId: 313232,
          data: data,
          headerType: 2,
          paramType: 0,
          isApiV2: false,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * 修改提现密码
     * */
    async resetWithdrawPassword(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      // 发起请求
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        ...payload,
        acct_pwd: getp(payload.acct_pwd),
        ori_acct_pwd: payload.ori_acct_pwd ? getp(payload.ori_acct_pwd) : '',
      };
      try {
        const res = await network.request({
          cmdId: 310122,
          data: data,
          headerType: 2,
          paramType: 0,
          isApiV2: false,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /*
     * 获取联系方式
     * */
    async fetchContact() {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      // 发起请求
      const data = [tenantInfo.value.tid, userInfo.value.cust_id];
      try {
        const res = await network.request({
          cmdId: 310230,
          data: data,
          headerType: 2,
          paramType: 2,
          isApiV2: false,
        });
        if (res && res.type === 'success') {
          this.contact = res.data;
          this.userInfo.custContact = { ...this.userInfo.custContact, ...this.contact };
          this.saveUser(this.userInfo);
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * @name 修改用户联系方式
     * @param payload.contact_name 姓名
     * @param payload.gender 性别 1:男 2:女
     * @param payload.contact_addr 联系地址
     * @param payload.company 公司
     * @param payload.home_phone 家用电话
     * @param payload.office_phone 办公电话
     * @param payload.mobile_phone 手机号码
     * @param payload.detail 详情
     * @param payload.email 电子邮箱
     * @param payload.qq QQ号码
     * @param payload.weixin 微信号码
     * @param payload.post_code 邮政编码
     * @param payload.post_addr 通信地址
     * @param payload.fax 传真
     */
    async reContact(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      const custContact: any = userInfo.value.custContact || {};
      // 发起请求
      const data = {
        ...payload,
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        contact_name: payload.contact_name || custContact.contact_name || '',
        email: payload.email || custContact.email || '',
        weixin: payload.weixin || custContact.weixin || '',
        qq: payload.qq || custContact.qq || '',
        mobile_phone: payload.mobile_phone || custContact.mobile_phone || '',
        contact_addr: payload.contact_addr || custContact.contact_addr || '',
        facebook: payload.facebook || custContact.facebook || '',
        whatsApp: payload.whatsApp || custContact.whatsApp || '',
        telegram: payload.telegram || custContact.telegram || '',
        mark: 1,
      };
      try {
        const res = await network.request({
          headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8' },
          cmdId: 310211,
          data: data,
          headerType: 2,
          paramType: 1,
          isApiV2: false,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },

    /**
     * @name 修改生日
     * @param payload.birthday 生日

     */
    async reBirthday(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      const custContact: any = userInfo.value.custContact || {};
      // 发起请求
      const data = {
        ...payload,
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
      };
      try {
        const res = await network.request({
          cmdId: 3310051,
          data: data,
          headerType: 2,
          paramType: 1,
          isApiV2: false,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },

    /**
     * 修改你昵称
     * */
    async reNickName(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;

      // 发起请求
      const data = {
        ...payload,
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        nickname: payload.nickname,
      };

      try {
        const res = await network.request({
          cmdId: 310113,
          data: data,
          headerType: 2,
          paramType: 0,
          isApiV2: false,
        });
        if (res && res.type === 'success') {
          if (res.data && res.data.response_code === '9999') {
            if (this.userInfo) {
              this.userInfo.nickname = payload.nickname;
            }
          }
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },

    // 量化交易 - vip等级
    async reqVIPList(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      // 发起请求
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        ...payload,
      };
      try {
        const res = await network.request({
          cmdId: 327053,
          data: data,
          headerType: 2,
          paramType: 0,
          isApiV2: false,
        });
        if (res && res.type === 'success') {
          this.vipList = res.data?.data || [];
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    // 量化交易 - vip等级领取
    async getReward(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      // 发起请求
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        receive_type: payload.receive_type,
        level: payload.level,
        amount: payload.amount,
        ...payload,
      };
      try {
        const res = await network.request({
          cmdId: 327054,
          data: data,
          headerType: 2,
          paramType: 0,
          isApiV2: false,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    // vip等级页面领取奖励和俸禄
    async getRewardMoney(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      // 发起请求
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        ...payload,
      };
      try {
        const res = await network.request({
          cmdId: 374001,
          data: data,
          // headerType: 2,
          // paramType: 0,
          // isApiV2: false
        });
        if (res?.data?.code === '1002') {
          return Promise.resolve(res.data);
        }
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    // vip奖励一键领取
    async getVipRewardAmount(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      // 发起请求
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        ...payload,
      };
      try {
        const res = await network.request({
          cmdId: 3740012,
          data: data,
          showError: true,
          // headerType: 2,
          // paramType: 0,
          // isApiV2: false
        });
        if (res?.type === 'success') {
          return Promise.resolve(res?.data);
        } else {
          return Promise.reject(res?.data);
        }
      } catch (error: any) {
        return Promise.reject(error?.data);
      }
    },
    // vip等级详情
    async getVipDetail(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo } = hotData;
      // 发起请求
      const data = {
        tid: tenantInfo.value.tid,
        option_code: 'VIP_ACTIVITY_RULE_CONFIG',
        ...payload,
      };
      try {
        const res = await network.request({
          cmdId: 370730,
          data: data,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    // 量化交易 - 邀请好友
    async getFirends(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      // 发起请求
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        ...payload,
      };
      try {
        const res = await network.request({
          cmdId: 327055,
          data: data,
          headerType: 2,
          paramType: 0,
          isApiV2: false,
        });
        if (res && res.type === 'success') {
          const resulate = res.data || [];
          const newArr: any[] = [];
          resulate.forEach(item => {
            // 分享领工资
            if (item.type === 106) {
              const shareIt: any[] = [];
              const standara = item.standara;
              const amount = standara.record;
              const arr: any[] = [];
              for (let z = 1; z < 11; z++) {
                const list: any = {};
                const stan2 = 'standara' + z;
                const standara_name = 'standara_name' + z;
                const icon_url = 'icon_url' + z;
                const explains = 'explains' + z;
                if (standara[stan2]) {
                  const listArr2 = standara[stan2].split(':');
                  list.fraction = Number(listArr2[0]); // 达标
                  list.reward = Number(listArr2[1]); // 奖金
                  list.standara_name = standara[standara_name];
                  list.icon_url = standara[icon_url];
                  list.explains = standara[explains];
                  list.is_receive = standara.is_receive || 0; // 是否领取上限 0不 1有
                  if (list.fraction !== 0) {
                    arr.push(list.fraction);
                    list.isReceive = 0;
                    list.percentage = 0;
                    list.startTime = item.eff_time;
                    list.endTime = item.exp_time;
                    shareIt.push(list);
                  }
                }
              }
              for (let x = 0; x < arr.length; x++) {
                const item = arr[x];
                if (item <= amount) {
                  shareIt[x].isReceive = 2;
                  shareIt[x].percentage = 100;
                }
                if (x === 0) {
                  if (amount === 0) {
                    shareIt[x].percentage = 0;
                  } else {
                    if (item === amount) shareIt[x].isReceive = 2;
                    shareIt[x].percentage = parseInt(String((amount / item) * 100));
                    if (shareIt[x].percentage > 100) shareIt[x].percentage = 100;
                  }
                } else if (item > amount && amount > arr[x - 1]) {
                  shareIt[x - 1].isReceive = 2;
                  shareIt[x].percentage = parseInt(String(((amount - arr[x - 1]) / (item - arr[x - 1])) * 100));
                }
                shareIt[x].completed = amount < 0 ? 0 : amount;
                if (standara.receive_amount) {
                  standara.receive_amount.forEach(item => {
                    if (Number(item.receive_level) - 1 === x) {
                      shareIt[x].isReceive = 1; // 已领取
                    }
                  });
                }
              }
              item.standara = shareIt;
              newArr.push(item);
            }
          });
          if (newArr && newArr.length > 0) {
            newArr.sort((a, b) => a.sort_v - b.sort_v);
          }
          return Promise.resolve(newArr);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    // 量化交易 - 领取邀请好友奖励
    async RecriveRescueMoney(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      // 发起请求
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        receive_level: payload.amount,
        type: payload.type,
        amount: payload.reward.toFixed(3) * 1000,
      };
      try {
        const res = await network.request({
          cmdId: 327056,
          data: data,
          headerType: 2,
          paramType: 0,
          isApiV2: false,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /*
     * 获取我的分享 - 佣金
     */
    async reqMyShareRebate(payload: any = {}) {
      if (payload.is_clear) {
        this.myShareRebate = {};
      }
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      // 发起请求
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        // partner_id: userInfo.value.cust_id,
      };
      try {
        const res = await network.request({
          cmdId: 324459, //324459
          data: data,
        });
        if (res && res.type === 'success') {
          this.myShareRebate = res.data;
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /*
     * 获取我的分享 - 佣金(推广中心)
     */
    async getMyShareRebate(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      // 发起请求
      const data = {
        tid: tenantInfo.value.tid,
        partner_id: userInfo.value?.partner?.partner_id || userInfo.value?.cust_id,
      };
      try {
        const res = await network.request({
          cmdId: 622009,
          data: data,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    // 我的分享 - 邀请好友页面
    async reqMyShare(payload: any = {}) {
      if (payload.is_clear) {
        this.myShareList = {};
      }
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      // 发起请求
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        ...payload,
      };
      try {
        const res = await network.request({
          cmdId: 311935,
          data: data,
          headerType: 2,
          paramType: 0,
          isApiV2: false,
        });
        if (res && res.type === 'success') {
          this.myShareList = res.data;
          console.log(this.myShareList, 'reqMyShare');
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * 获取邀请码
     */
    async reqInvicode(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo } = hotData;
      // 发起请求
      const data = {
        ...payload,
        link_addr: payload.link_addr,
        tid: tenantInfo.value.tid,
      };
      try {
        const res = await network.request({
          cmdId: 313032,
          data: data,
          headerType: 1,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },

    // 我的佣金（多种模式）
    async reqCommissionList(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      // 发起请求
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        num_per_page: 10,
        ...payload,
      };
      try {
        const res = await network.request({
          cmdId: 382234,
          data: data,
          headerType: 2,
          paramType: 0,
          isApiV2: false,
        });
        if (res && res.type === 'success') {
          this.commissionList = res.data.data;
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    async reqMyCommission(payload: any = {}) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      if (payload.is_clear) {
        this.myCommissionlist = [];
      }
      // 发起请求
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
      };
      try {
        const res = await network.request({
          cmdId: 311936,
          data: data,
          headerType: 2,
          paramType: 0,
          isApiV2: true,
        });
        if (res && res.type === 'success') {
          this.myCommissionlist = res.data;
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },

    /** 活动奖金领取接口 */
    async reqBonusPickup(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        task_id: payload.task_id,
        id: payload.id,
      };
      try {
        const res = await network.request({
          cmdId: 377210,
          data: data,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * @name 查询用户推荐工资数据
     */
    async reqUserRecSalaryList(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;

      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        task_id: payload.task_id,
      };

      try {
        const res = await network.request({
          cmdId: 396100,
          data: data,
          headerType: 2,
          paramType: 0,
        });
        if (res && res.type === 'success') {
          console.log(res.data, 'reqUserRecSalaryList');
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * @name 领取推荐工资数据
     */
    async userGetRecSalary(payload) {
      const { hotData } = useHot();
      const { tenantInfo, userInfo } = hotData;
      const data = {
        tid: tenantInfo.value.tid,
        cust_id: userInfo.value.cust_id,
        type: 106,
        ...payload,
      };
      try {
        const res = await network.request({
          cmdId: 396101,
          data: data,
          headerType: 2,
          paramType: 0,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res.data);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
    /**
     * 查询用户密码是否为空
     * */
    async reqHasPassword(payload: any = {}) {
      const { userInfo } = storeToRefs(useUserStore());
      const { tenantInfo } = storeToRefs(useTenantStore());
      try {
        const type = payload?.type || 2;
        const res: any = await userServices.reqHasPassword({
          ...payload,
          tid: tenantInfo.value.tid,
          cust_id: userInfo.value.cust_id,
          type, // 1登录密码 2支付密码
        });
        // data: 0无密码 1有密码
        if (type === 1) {
          this.hasLoginPassword = Number(res?.data) === 1;
        } else if (type === 2) {
          this.hasPayPassword = Number(res?.data) === 1;
        }
        return Promise.resolve(res?.data);
      } catch (error: any) {
        return Promise.reject(error?.data);
      }
    },

    /**
     * 获取个人信息手机显示开关
     */
    async getCusPhoneDisplay() {
      const { tenantInfo } = storeToRefs(useTenantStore());
      const data = {
        tid: tenantInfo.value.tid,
        option_code: 'CUST_PHONE_DISPLAY_SWITCH',
      };
      try {
        const res: any = await network.request({
          cmdId: 370730,
          data: data,
        });
        if (res && res.type === 'success') {
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res);
        }
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    },
  },
});
